import React, { useRef, useEffect } from 'react'

import {
  array,
  shape,
  number,
  string,
  func,
  bool,
} from 'prop-types'

import systemMessageAvatarUrl from 'images/avatar/job_assistant.jpg'

import Message from './Message'
import MessageFormContainer from './MessageFormContainer'

function MainContent({
  conversationId,
  isSuspendedMessaging,
  isJobConversation = false,
  isLiveMessaging = false,
  isAttachmentsHidden,
  messages,
  onSubmit,
  scrollBottomOffset,
  onLoadMore,
  meta,
  isLoadingMore,
  conversationType,
  included,
}) {
  const channelRef = useRef(null)
  const isUnmountedRef = useRef(false)
  const scrollableEl = useRef(null)

  useEffect(() => {
    scrollToBottomOffset()

    if (isLiveMessaging) createChannel()
  }, [messages])

  useEffect(() => () => {
    isUnmountedRef.current = true
    channelRef.current?.unsubscribe?.()
    channelRef.current = null
  }, [])

  function createChannel() {
    if (isUnmountedRef.current) return
    if (channelRef.current) return
    if (!window.App.cable?.subscriptions) return

    channelRef.current = window.App.cable.subscriptions.create({
      channel: 'TypingChannel',
      conversation_id: conversationId,
    }, {
      connected: () => {},
      received: () => {},
    })
  }

  function updateTypingState(condition) {
    if (typeof channelRef.current?.perform !== 'function') {
      if (isLiveMessaging) createChannel()
      return
    }

    const action = condition ? 'types' : 'stopped_typing'
    channelRef.current.perform(action, { conversation_id: conversationId })
  }

  function scrollToBottomOffset() {
    const height = scrollableEl.current.scrollHeight
    scrollableEl.current.scrollTop = height - scrollBottomOffset
  }

  function handleLoadMore() {
    onLoadMore(scrollableEl.current.scrollHeight)
  }

  const emailLink = (
    <a
      className="link"
      href="/profile/settings#tab-settings-notifications"
    >
      Review my email preferences
    </a>
  )

  const showLoadMore = !(Number(meta.currentPage || 0) === Number(meta.totalPages || 0))

  return (
    <div className="conversation__main-content">
      <div className="main-content__messages" ref={scrollableEl}>
        {showLoadMore && (
          <div className="u-text-center">
            <button
              className="button button--tiny button--longer"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              Load more messages
            </button>
          </div>
        )}
        {messages.map((message, index) => {
          return (
            <Message
              key={index}
              message={message}
              included={included}
              isFirstUnreadMessage={meta.firstUnreadMessageId === parseInt(message.id, 10)}
              systemMessageAvatarUrl={message.attributes.systemMessage ? systemMessageAvatarUrl : ''}
            />
          )
        })}

        {conversationType === 'promotions' && (
          <div className="main-content__promotions-unsubscribe text-extra-small">
            {'Why am I seeing this? '}
            {emailLink}
          </div>
        )}
      </div>
      <div className="main-content__send-message card card--flat">
        <MessageFormContainer
          conversationId={conversationId}
          isSuspendedMessaging={isSuspendedMessaging}
          isAttachmentsHidden={isAttachmentsHidden}
          onSubmit={onSubmit}
          isLiveMessaging={isLiveMessaging}
          updateTypingState={updateTypingState}
          isJobConversation={isJobConversation}
        />
        {isSuspendedMessaging && (
          <div className="main-content__suspended-messaging">{window.I18n.t('users.suspended_messaging')}</div>
        )}
      </div>
    </div>
  )
}

MainContent.propTypes = {
  messages: array.isRequired,
  included: array.isRequired,
  meta: shape({
    currentPage: number.isRequired,
    totalPages: number.isRequired,
  }).isRequired,
  conversationId: string.isRequired,
  onLoadMore: func.isRequired,
  onSubmit: func.isRequired,
  scrollBottomOffset: number.isRequired,
  isLoadingMore: bool.isRequired,
  isSuspendedMessaging: bool.isRequired,
  isAttachmentsHidden: bool.isRequired,
  isLiveMessaging: bool,
  isJobConversation: bool,
  conversationType: string.isRequired,
}

export default MainContent
